import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection } from 'firebase/firestore';
import { db } from '../../../../firebase-client';
import { safeOnSnapshot } from '../../../../utils/firestoreSubscriptions';
import { listAccessibleBoards } from '../../../../utils/boardApi';

function timeValue(value) {
  if (!value) return 0;
  if (typeof value?.toMillis === 'function') return value.toMillis();
  if (typeof value?.toDate === 'function') return value.toDate().getTime();
  if (value instanceof Date) return value.getTime();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
}

function formatDate(value) {
  const millis = timeValue(value);
  if (!millis) return 'No activity yet';
  return new Date(millis).toLocaleString(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

function formatSource(value) {
  return String(value || 'review').replaceAll('_', ' ');
}

function getReasonLabel(reason) {
  if (typeof reason === 'string') return reason;
  return reason?.label || reason?.id || '';
}

function clamp(value = '', max = 120) {
  const text = String(value || '').trim();
  if (text.length <= max) return text;
  return `${text.slice(0, max - 3).trim()}...`;
}

function sortByActivity(items = []) {
  return [...items].sort((left, right) => {
    const diff = timeValue(right?.lastActivityAt || right?.updatedAt || right?.createdAt)
      - timeValue(left?.lastActivityAt || left?.updatedAt || left?.createdAt);
    if (diff !== 0) return diff;
    return String(left?.name || left?.id || '').localeCompare(String(right?.name || right?.id || ''));
  });
}

function SettingsIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M19.14 12.94a7.77 7.77 0 0 0 .05-.94 7.77 7.77 0 0 0-.05-.94l2.03-1.58a.5.5 0 0 0 .12-.64l-1.92-3.32a.5.5 0 0 0-.6-.22l-2.39.96a7.06 7.06 0 0 0-1.63-.94l-.36-2.54a.5.5 0 0 0-.49-.42h-3.84a.5.5 0 0 0-.49.42l-.36 2.54c-.58.23-1.12.54-1.63.94l-2.39-.96a.5.5 0 0 0-.6.22L2.7 8.84a.5.5 0 0 0 .12.64l2.03 1.58a7.77 7.77 0 0 0-.05.94c0 .32.02.63.05.94L2.82 14.52a.5.5 0 0 0-.12.64l1.92 3.32a.5.5 0 0 0 .6.22l2.39-.96c.5.4 1.05.71 1.63.94l.36 2.54a.5.5 0 0 0 .49.42h3.84a.5.5 0 0 0 .49-.42l.36-2.54a7.06 7.06 0 0 0 1.63-.94l2.39.96a.5.5 0 0 0 .6-.22l1.92-3.32a.5.5 0 0 0-.12-.64l-2.03-1.58zM12 15.5A3.5 3.5 0 1 1 12 8.5a3.5 3.5 0 0 1 0 7z"/>
    </svg>
  );
}

function isEventCommentsBoard(board = {}) {
  return board?.systemType === 'event_comments' || board?.id === 'events';
}

function isDiscussionBoard(board = {}) {
  return !isEventCommentsBoard(board);
}

function buildBoardsLink(teamId, overrides = {}) {
  const rawParams = {
    view: 'boards',
    clubId: teamId,
    teamId,
    ...overrides,
  };
  const params = new URLSearchParams();
  Object.entries(rawParams).forEach(([key, value]) => {
    if (value == null || value === '') return;
    params.set(key, String(value));
  });
  return `/messages?${params.toString()}`;
}

function BoardRow({ teamId, board }) {
  const topicCount = Number(board?.topicCount || 0);
  const replyCount = Number(board?.replyCount || 0);
  return (
    <Link
      to={buildBoardsLink(teamId, { boardId: board.id, panel: 'discussion' })}
      className="message-boards-card__row"
    >
      <div className="message-boards-card__row-title">{board.name || 'Untitled board'}</div>
      <div className="message-boards-card__row-meta text-muted small">
        <span>{topicCount} topic{topicCount === 1 ? '' : 's'} • {replyCount} repl{replyCount === 1 ? 'y' : 'ies'}</span>
        <span>{formatDate(board.lastActivityAt || board.updatedAt || board.createdAt)}</span>
      </div>
    </Link>
  );
}

function ReviewCaseRow({ teamId, caseEntry }) {
  const firstReason = Array.isArray(caseEntry?.reasons)
    ? caseEntry.reasons.map(getReasonLabel).find(Boolean)
    : '';
  const preview = firstReason
    || caseEntry?.contentPreview
    || caseEntry?.hiddenContentSnapshot?.bodyText
    || 'Needs club review.';

  return (
    <Link
      to={buildBoardsLink(teamId, {
        boardId: caseEntry.boardId || '',
        topicId: caseEntry.topicId || '',
        panel: 'review',
        caseId: caseEntry.id || '',
      })}
      className="message-boards-card__row"
    >
      <div className="message-boards-card__row-title">{caseEntry.boardName || 'Board'}</div>
      <div className="message-boards-card__row-meta text-muted small">
        <span>{formatSource(caseEntry.source)} • {clamp(preview, 90)}</span>
        <span>{formatDate(caseEntry.updatedAt || caseEntry.createdAt)}</span>
      </div>
    </Link>
  );
}

export default function MessageBoardsCard({
  teamId,
  isAdmin = false,
  openReviewCases = [],
  reviewCasesLoading = false,
  onOpenGifSettings,
}) {
  const [boards, setBoards] = useState([]);
  const [boardsLoading, setBoardsLoading] = useState(false);

  useEffect(() => {
    if (!teamId) {
      setBoards([]);
      setBoardsLoading(false);
      return undefined;
    }

    setBoardsLoading(true);

    if (isAdmin) {
      const unsubscribe = safeOnSnapshot(
        collection(db, 'teams', teamId, 'boards'),
        (snap) => {
          setBoards(sortByActivity(snap.docs.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })).filter(isDiscussionBoard)));
          setBoardsLoading(false);
        },
        () => {
          setBoardsLoading(false);
        },
        { label: 'message-boards-admin', teamId }
      );
      return () => unsubscribe();
    }

    let cancelled = false;
    listAccessibleBoards({ teamId })
      .then((result) => {
        if (cancelled) return;
        const nextBoards = Array.isArray(result?.boards)
          ? result.boards.filter(isDiscussionBoard)
          : [];
        setBoards(sortByActivity(nextBoards));
      })
      .catch(() => {
        if (!cancelled) setBoards([]);
      })
      .finally(() => {
        if (!cancelled) setBoardsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isAdmin, teamId]);

  const visibleBoards = boards.slice(0, 3);
  const visibleReviewCases = useMemo(
    () => sortByActivity(openReviewCases).slice(0, 3),
    [openReviewCases]
  );
  const openReviewCount = openReviewCases.length;
  const primaryLink = openReviewCount > 0 && isAdmin
    ? buildBoardsLink(teamId, { panel: 'review' })
    : buildBoardsLink(teamId, { panel: 'discussion' });

  return (
    <div className="team-card message-boards-card">
      <div className="card-header-with-icon">
        <span className="card-title">Message Boards</span>
        {isAdmin && (
          <button
            type="button"
            className="edit-btn"
            onClick={() => onOpenGifSettings?.()}
            aria-label="Board settings"
            title="Board settings"
          >
            <SettingsIcon />
          </button>
        )}
      </div>

      <div className="card-body">
        <div className="message-boards-card__summary">
          <div className="message-boards-card__summary-text text-muted">
            {boardsLoading ? 'Loading boards...' : (
              <>
                <strong>{boards.length}</strong> accessible board{boards.length === 1 ? '' : 's'}
                {isAdmin && (
                  <>
                    {' '}• <strong>{reviewCasesLoading ? '...' : openReviewCount}</strong> open review case{openReviewCount === 1 ? '' : 's'}
                  </>
                )}
              </>
            )}
          </div>

          <Link to={primaryLink} className="btn btn--primary" style={{ whiteSpace: 'nowrap' }}>
            {openReviewCount > 0 && isAdmin ? 'Review Queue' : 'Open Boards'}
          </Link>
        </div>

        {isAdmin && openReviewCount > 0 && (
          <>
            <div className="text-muted small" style={{ marginTop: 12, marginBottom: 6 }}>
              Review cases
            </div>
            <div className="message-boards-card__list">
              {visibleReviewCases.map((caseEntry) => (
                <ReviewCaseRow key={caseEntry.id} teamId={teamId} caseEntry={caseEntry} />
              ))}
            </div>
            {openReviewCount > visibleReviewCases.length && (
              <p className="text-muted small" style={{ marginTop: 8 }}>
                + {openReviewCount - visibleReviewCases.length} more review case{openReviewCount - visibleReviewCases.length === 1 ? '' : 's'}
              </p>
            )}
          </>
        )}

        <div className="text-muted small" style={{ marginTop: 12, marginBottom: 6 }}>
          Recent boards
        </div>
        {boardsLoading ? (
          <p className="text-muted small" style={{ marginTop: 0 }}>Loading recent boards...</p>
        ) : visibleBoards.length > 0 ? (
          <div className="message-boards-card__list">
            {visibleBoards.map((board) => (
              <BoardRow key={board.id} teamId={teamId} board={board} />
            ))}
          </div>
        ) : (
          <p className="text-muted small" style={{ marginTop: 0 }}>
            No boards are available yet.
          </p>
        )}

        {openReviewCount > 0 && isAdmin && (
          <div className="form-actions" style={{ justifyContent: 'flex-start', marginTop: 12 }}>
            <Link to={buildBoardsLink(teamId, { panel: 'discussion' })} className="btn btn--ghost btn--sm">
              Open Boards
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
